/**
 * WP-8 combat viewer — the polish centerpiece. A theatrical side-view sea battle
 * (VISUAL-SPEC "Combat viewer"): big ship sprites on a royal-blue brick sea,
 * muzzle flashes, shell arcs, torpedo wakes, carrier seaplanes with a "+1 Air"
 * badge, explosions, rising damage-float text, sinking ships, and screen shake.
 * A bottom panel lists every combat site (#1 crcr dddd…); controls give
 * pause / speed / prev-next duel / skip, plus a "skip future battles" setting.
 *
 * It animates the per-site results from the rules engine (rules/combat.ts
 * CombatSite[]); the engine has already mutated G, so this is a deterministic
 * replay. All timing + effect seeds come from ui/combat-timeline.ts (pure), so a
 * given (sites, seed, time) repaints the exact same frame — seek() + pause()
 * give the golden screenshot a fixed keyframe.
 */
import { makeRng } from '../rules/rng';
import { UNITS } from '../rules/ships';
import type { Side } from '../rules/state';
import type { CombatEvent, CombatShipSnapshot, CombatSite } from '../rules/combat';
import {
  beatAt, beatProgress, buildTimeline, hpAfter, type Beat, type ShotBeat, type Timeline,
} from './combat-timeline';
import { getSkipCinematic, setSkipCinematic } from './save';
import { audio } from '../audio/engine';
import { ICON_PLAY, ICON_PAUSE, ICON_NEXT, ICON_SWORDS } from './icons';
import type { ShipType } from '../rules/state';
import { Application, Container, Graphics, Sprite, TilingSprite, Texture, TextureSource, Text } from '../render/pixi';
import { Assets } from '../render/pixi';
import { shipArtUrl, ensignUrl, ensignLayout, type Facing } from '../render/geometry';

// The scene is a fixed design WIDTH; its HEIGHT is computed at mount/resize from
// the stage's aspect ratio (see fit()) so the canvas FILLS the stage with no dead
// letterbox margins — the old fixed 2.4:1 canvas left big dark bands in the window.
// WATER_Y follows SCENE_H by WATER_FRAC so the sky/water split stays constant.
const SCENE_W = 960;
const WATER_FRAC = 0.42; // the HORIZON as a fraction of scene height — a high sea line: sky above, open sea filling the foreground below
const SHIP_FRAC = 0.82; // ships sit low in the FOREGROUND sea, well below the horizon (decoupled from WATER_Y so a high sea line doesn't drag them up)
let SCENE_H = 400; // recomputed in fit()
let WATER_Y = SCENE_H * WATER_FRAC;
let SHIP_Y = SCENE_H * SHIP_FRAC;
const IMPACT = 0.5; // fraction of a shot beat at which the projectile lands

const COL = {
  R: '#e0584a',
  Y: '#e9c247',
  Rlabel: '#ff9d8f',
  Ylabel: '#ffd76e',
};
const BIG = new Set(['CV', 'BB', 'CR']);

// Team chips — the game's neutral identity (colour + shape), NOT the words
// "Red"/"Yellow". Mirrors game.ts SHAPE_O/SHAPE_X + .ar-team-chip so the battle
// screen reads the same as the scoreboard, prediction line, and instant chooser.
const SHAPE_O = '<svg viewBox="0 0 12 12" aria-hidden="true"><circle cx="6" cy="6" r="3.4" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
const SHAPE_X = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3.2 3.2 L8.8 8.8 M8.8 3.2 L3.2 8.8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
const teamChip = (s: Side): string => `<i class="ar-cine-team ${s}">${s === 'R' ? SHAPE_O : SHAPE_X}</i>`;

/** on-screen sprite width (px) per class — capitals dwarf escorts. Kept modest so
 * the ships read as vessels on a wide sea, not billboards; every width is also
 * clamped to MAX_CINE_W so no single ship dominates the scene. */
const CINE_W: Record<string, number> = {
  CV: 190, BB: 198, CR: 152, SS: 118, DD: 122, PT: 80, AT: 108, TR: 100, FT: 122,
};
/** hard cap on on-screen sprite width (px) — no ship renders larger than this */
const MAX_CINE_W = 190;

const CSS = `
/* Self-contained copy of the game's palette (game.ts .ar-game): the cinematic's
   <style> is global (and the golden mounts it outside .ar-game), so it can't
   inherit the tokens — but it MUST match, so the battle screen reads like the
   rest of the game (navy chrome / parchment tray / brass buttons / team chips). */
.ar-cine { --navy:hsl(217, 53.7%, 16.1%); --navy2:hsl(220, 60%, 11.8%); --ink:hsl(219, 58.3%, 9.4%);
  --parch:hsl(44, 53.7%, 83.9%); --parch2:hsl(40, 46.9%, 71.2%); --wood:hsl(31, 47.6%, 28.4%); --wood-d:hsl(30, 54.2%, 18.8%);
  --brass:hsl(43, 63.2%, 58.4%); --brass-l:hsl(46, 80.7%, 73.5%); --brass-d:hsl(42, 61.6%, 34.7%);
  --txt:hsl(28, 55.6%, 10.6%); --head:hsl(23, 66.7%, 21.2%); --redx:hsl(7, 70.6%, 41.4%); --yelx:hsl(44, 84.4%, 37.6%);
  position: absolute; inset: 0; z-index: 25; display: flex; flex-direction: column;
  background: radial-gradient(ellipse at 50% 40%, hsl(219, 51.8%, 16.3%) 0%, hsl(219, 54.8%, 6.1%) 100%);
  font-family: Georgia, 'Times New Roman', serif; color: hsl(45, 100%, 90.6%); user-select: none; }

/* header — the navy command bar, matching game.ts .ar-top */
.ar-cine-hd { flex: 0 0 auto; padding: 8px 16px; display: flex; align-items: center; gap: 14px;
  background: linear-gradient(180deg, hsl(219, 52.2%, 22.2%), var(--navy) 60%, var(--navy2));
  border-bottom: 3px solid var(--brass-d); }
.ar-cine-hd .t { font-weight: 700; font-size: 15px; color: var(--brass-l); letter-spacing: 0.4px;
  text-shadow: 0 1px 0 hsl(0, 0%, 0%); white-space: nowrap; flex: 0 0 auto; }
.ar-cine-hd .t svg { width: 1.05em; height: 1.05em; vertical-align: -0.15em; margin-right: 5px; }
.ar-cine-hd .s { font-size: 13.5px; color: hsl(215, 100%, 90.6%); font-style: italic; flex: 1 1 auto; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* fleet tally — the battle scoreboard: each side's ships still afloat in this
   duel, shown by its team chip + count (drops live as ships sink). */
.ar-cine-tally { flex: 0 0 auto; display: flex; align-items: center; gap: 12px; }
.ar-cine-tally .team { display: inline-flex; align-items: center; gap: 5px; font-weight: 800; font-size: 15px;
  color: hsl(45, 100%, 92%); font-variant-numeric: tabular-nums; text-shadow: 0 1px 2px hsla(0, 0%, 0%, 0.7); }
.ar-cine-tally .team.gone { opacity: 0.4; text-decoration: line-through; }
.ar-cine-team { width: 16px; height: 16px; border-radius: 3px; flex: 0 0 auto; border: 1px solid hsla(0, 0%, 0%, 0.55);
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.45), 0 1px 2px hsla(0, 0%, 0%, 0.4);
  display: inline-flex; align-items: center; justify-content: center; vertical-align: -3px; }
.ar-cine-team svg { width: 72%; height: 72%; display: block; }
.ar-cine-team.R { background: linear-gradient(180deg, hsl(7, 72.2%, 56.3%), var(--redx)); color: hsl(0, 0%, 100%); }
.ar-cine-team.Y { background: linear-gradient(180deg, hsl(44, 86.3%, 68.6%), var(--yelx)); color: hsl(42, 81.3%, 12.5%); }

/* clip the stage: the battle canvas is a fixed 920×460, so on a short viewport
   (or the 980×600 golden host) it can be taller than this flex cell — overflow
   hidden keeps it from bleeding up over the header or down over the footer. */
.ar-cine-stage { flex: 1 1 0; display: flex; align-items: center; justify-content: center; min-height: 0; padding: 10px; overflow: hidden; }
.ar-cine-stage canvas { image-rendering: auto; border-radius: 3px;
  box-shadow: 0 6px 26px hsla(0, 0%, 0%, 0.55); border: 2px solid var(--ink); outline: 1px solid hsla(46, 80.7%, 73.5%, 0.15); }

/* footer — the parchment control tray, matching game.ts .ar-bottom */
.ar-cine-ft { flex: 0 0 auto; display: flex; align-items: stretch;
  background: linear-gradient(180deg, hsl(45, 57.1%, 86.3%), var(--parch) 30%, var(--parch2));
  border-top: 3px solid var(--brass-d); color: var(--txt); }
.ar-cine-sites { flex: 1 1 auto; padding: 10px 12px; display: flex; flex-wrap: wrap; gap: 7px; align-content: flex-start;
  overflow: auto; border-right: 2px solid var(--wood); }
.ar-cine-sites-cap { flex: 0 0 100%; font-size: 10px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase;
  color: var(--wood); margin-bottom: 1px; }
.ar-cine-chip { font-family: 'Courier New', monospace; font-size: 13px; font-weight: 700; cursor: pointer;
  padding: 5px 8px; border: 1px solid var(--wood); border-radius: 4px; background: hsla(0, 0%, 100%, 0.45);
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.55); letter-spacing: 1px; white-space: nowrap; transition: filter 0.08s; }
.ar-cine-chip:hover { filter: brightness(1.05); }
.ar-cine-chip .n { color: var(--head); margin-right: 5px; }
.ar-cine-chip .R { color: var(--redx); } .ar-cine-chip .Y { color: var(--yelx); }
.ar-cine-chip .gap { color: var(--wood); margin: 0 2px; }
.ar-cine-chip.on { background: var(--head); box-shadow: 0 0 0 2px var(--brass-l); }
.ar-cine-chip.on .n { color: var(--brass-l); }
.ar-cine-chip.on .R, .ar-cine-chip.on .Y, .ar-cine-chip.on .gap { filter: brightness(1.45); }
.ar-cine-ctrl { flex: 0 0 auto; width: 320px; padding: 10px 12px; display: flex; flex-direction: column; gap: 7px; justify-content: center; }
.ar-cine-row { display: flex; gap: 7px; }
/* buttons — identical to game.ts .ar-btn (was under-sized: 13px / 6px 4px) */
.ar-cine-btn { flex: 1; font-family: inherit; font-weight: 700; font-size: 15px; padding: 9px 8px; cursor: pointer; color: var(--txt);
  background: linear-gradient(180deg, var(--brass-l), var(--brass) 55%, var(--brass-d));
  border: 2px solid var(--wood-d); border-radius: 4px;
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.55), inset 0 -2px 2px hsla(0, 0%, 0%, 0.18), 0 1px 2px hsla(0, 0%, 0%, 0.3);
  text-shadow: 0 1px 0 hsla(0, 0%, 100%, 0.35); transition: filter 0.08s; white-space: nowrap; }
.ar-cine-btn svg { width: 1.05em; height: 1.05em; vertical-align: -0.16em; }
.ar-cine-btn:hover { filter: brightness(1.06); }
.ar-cine-btn:active { box-shadow: inset 0 2px 4px hsla(0, 0%, 0%, 0.35); transform: translateY(1px); }
.ar-cine-btn.primary { color: hsl(90, 100%, 95.7%); background: linear-gradient(180deg, hsl(105, 47.8%, 64.7%), hsl(105, 38.4%, 43.9%) 55%, hsl(105, 41.7%, 29.6%)); border-color: hsl(110, 40.6%, 25.1%); text-shadow: 0 1px 0 hsla(0, 0%, 0%, 0.3); }
.ar-cine-set { font-size: 12px; color: var(--wood-d); display: flex; align-items: center; gap: 6px; cursor: pointer; padding-top: 1px; }
.ar-cine-set input { accent-color: var(--brass-d); }
.ar-cine-bar { height: 5px; background: hsla(0, 0%, 0%, 0.28); border-radius: 3px; overflow: hidden; border: 1px solid hsla(0, 0%, 0%, 0.3); }
.ar-cine-bar i { display: block; height: 100%; background: linear-gradient(90deg, var(--brass), var(--brass-l)); width: 0; transition: width 0.1s linear; }
/* phones: stack the footer (battle-site chips over the transport controls)
   instead of placing the 320px control column beside them. */
@media (max-width: 699px) {
  .ar-cine-hd { gap: 10px; padding: 7px 12px; }
  .ar-cine-hd .t { font-size: 13.5px; }
  .ar-cine-tally .team { font-size: 13.5px; }
  .ar-cine-ft { flex-direction: column; }
  .ar-cine-sites { border-right: none; border-bottom: 2px solid var(--wood); max-height: 30vh; }
  .ar-cine-ctrl { width: auto; }
}
`;

export interface CombatViewerOpts {
  container: HTMLElement;
  sites: CombatSite[];
  viewSide: Side;
  seed: number;
  onDone: () => void;
  /** false → mount paused at t=0 (deterministic goldens); default true */
  autoplay?: boolean;
}

export interface CombatViewerHandle {
  readonly el: HTMLElement;
  readonly duration: number;
  seek(ms: number): void;
  pause(): void;
  play(): void;
  setSpeed(mult: number): void;
  gotoSite(i: number): void;
  skip(): void;
}

interface ShipBox {
  id: number;
  snap: CombatShipSnapshot;
  side: Side;
  x: number;
  cy: number;
  w: number;
  h: number;
  faceDir: 1 | -1; // +1 faces right
}

// Realistic renders come in true -left/-right facings, picked by which side of
// the duel the ship fights on (yours = left flank, steaming right) — no mirror
// flip, so lighting stays honest. The fort stays on the legacy side-baked
// vector art, which faces right and mirrors via scale. A submerged sub is the
// same submarine render drawn ghosted (translucent + cold tint, no ensign).
const legacyArt = (s: CombatShipSnapshot): boolean => !shipArtUrl(s.type, 'right');
function spriteUrl(s: CombatShipSnapshot, facing: Facing): string {
  const side = s.side === 'R' ? 'r' : 'y';
  return shipArtUrl(s.type, facing) ?? `assets/sprites/ship_${s.type.toLowerCase()}_${side}.png`;
}

export async function startCombatViewer(opts: CombatViewerOpts): Promise<CombatViewerHandle> {
  const { container, sites, viewSide, onDone } = opts;
  if (!document.getElementById('ar-cine-style')) {
    const style = document.createElement('style');
    style.id = 'ar-cine-style';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const tl: Timeline = buildTimeline(sites, { seed: opts.seed });
  // your fleet holds the left flank and faces right; the enemy faces left
  const facingOf = (s: CombatShipSnapshot): Facing => (s.side === viewSide ? 'right' : 'left');
  const spriteUrls = [...new Set([
    ...sites.flatMap((s) => s.before.map((snap) => spriteUrl(snap, facingOf(snap)))),
    ensignUrl('left'), ensignUrl('right'), 'assets/sprites/sea.jpg',
  ])];
  // mipmaps for the big renders (see board-gl.ts note — must be set pre-load)
  TextureSource.defaultOptions.autoGenerateMipmaps = true;
  await Assets.load(spriteUrls);
  const tex = (url: string): Texture => Assets.get(url) as Texture;

  // ---- DOM scaffold ----
  const root = document.createElement('div');
  root.className = 'ar-cine';
  root.id = 'ar-cine';
  root.innerHTML = `
    <div class="ar-cine-hd">
      <span class="t" id="ar-cine-title">${ICON_SWORDS}Battle</span>
      <span class="s" id="ar-cine-sub"></span>
      <div class="ar-cine-tally" id="ar-cine-tally"></div>
    </div>
    <div class="ar-cine-stage"></div>
    <div class="ar-cine-ft">
      <div class="ar-cine-sites" id="ar-cine-sites"></div>
      <div class="ar-cine-ctrl">
        <div class="ar-cine-bar"><i id="ar-cine-prog"></i></div>
        <div class="ar-cine-row">
          <button class="ar-cine-btn" id="ar-cine-prev" title="Previous duel">◀ Duel</button>
          <button class="ar-cine-btn" id="ar-cine-pause">${ICON_PAUSE} Pause</button>
          <button class="ar-cine-btn" id="ar-cine-next" title="Next duel">Duel ▶</button>
        </div>
        <div class="ar-cine-row">
          <button class="ar-cine-btn" id="ar-cine-speed">Speed 1×</button>
          <button class="ar-cine-btn" id="ar-cine-skip">Skip ${ICON_NEXT}</button>
        </div>
        <label class="ar-cine-set"><input type="checkbox" id="ar-cine-skipfut"> Skip future battles</label>
      </div>
    </div>`;
  container.appendChild(root);

  const stage = root.querySelector<HTMLElement>('.ar-cine-stage')!;
  const canvas = document.createElement('canvas');
  canvas.style.width = `${SCENE_W}px`;
  canvas.style.height = `${SCENE_H}px`;
  stage.appendChild(canvas);
  const automated = navigator.webdriver;
  const app = new Application();
  await app.init({
    canvas, width: SCENE_W, height: SCENE_H,
    backgroundColor: '#0e1830', antialias: true,
    resolution: automated ? 1 : Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true, preference: 'webgl',
  });
  app.ticker.stop(); // the combat clock drives rendering via render(ms)
  const $ = (id: string): HTMLElement => root.querySelector(`#${id}`)!;

  // Layers (screen-shake offsets shakeRoot). The brick sea is STATIC — drawn ONCE;
  // ships are retained sprites whose transforms update per frame; only the single
  // transient active-shot effect set redraws each frame (cheap). This is the perf
  // win over the Canvas2D version, which refilled the sea + redrew every ship/frame.
  const shakeRoot = app.stage.addChild(new Container());
  const seaG = shakeRoot.addChild(new Graphics());        // backdrop: sky + deep-water gradient
  // sea SURFACE detail (ripple texture + a foam line at the waterline) — drawn
  // BEHIND the ships so the iso ship renders sit cleanly ON TOP of the water (like
  // the board), rather than having translucent water painted over their hulls.
  const waterFg = shakeRoot.addChild(new Container());
  const shipLayer = shakeRoot.addChild(new Container()); // ships ON TOP of the sea
  const hpG = shakeRoot.addChild(new Graphics());
  const fxG = shakeRoot.addChild(new Graphics());
  const glowLayer = shakeRoot.addChild(new Container());
  const textLayer = shakeRoot.addChild(new Container());

  // soft radial glow, baked once → reused (tinted/scaled/alpha'd, additive) for the
  // muzzle flash and explosion core.
  const glowTex = ((): Texture => {
    const g = new Graphics();
    const R = 48;
    for (let i = 14; i >= 1; i--) g.circle(R, R, (R * i) / 14).fill({ color: 0xffffff, alpha: 0.08 });
    const t = app.renderer.generateTexture(g);
    g.destroy();
    return t;
  })();
  const mkGlow = (): Sprite => {
    const s = glowLayer.addChild(new Sprite(glowTex));
    s.anchor.set(0.5); s.blendMode = 'add'; s.visible = false;
    return s;
  };
  const muzzleGlow = mkGlow();
  const explosionGlow = mkGlow();

  const mkText = (size: number): Text => {
    const t = textLayer.addChild(new Text({
      text: '', anchor: 0.5, visible: false,
      style: { fontFamily: 'Georgia, serif', fontSize: size, fontWeight: 'bold', fill: '#ffffff', stroke: { color: 'rgba(0,0,0,0.7)', width: 3 }, align: 'center' },
    }));
    return t;
  };
  const dmgText = mkText(22);
  const airText = mkText(13);

  // ship sprite pool for the CURRENT site (rebuilt only when the site changes).
  // Each ship is a container: the base render + (realistic art only) the white
  // ensign overlay tinted with the side colour, carrying the team glyph.
  const shipSprites = new Map<number, Container>();
  let builtSite = -1;
  function ensureShips(si: number): void {
    if (si === builtSite) return;
    builtSite = si;
    for (const c of shipLayer.removeChildren()) c.destroy({ children: true });
    shipSprites.clear();
    if (si < 0) return;
    shipLayer.sortableChildren = true; // bigger ships (higher zIndex) render in front
    for (const box of layoutFor(si).values()) {
      const facing = facingOf(box.snap);
      const node = shipLayer.addChild(new Container());
      const sp = new Sprite(tex(spriteUrl(box.snap, facing)));
      sp.anchor.set(0.5);
      sp.width = box.w; sp.height = box.h;
      if (legacyArt(box.snap) && box.faceDir < 0) sp.scale.x = -sp.scale.x; // legacy art faces right → mirror
      const submerged = box.snap.type === 'SS' && box.snap.submerged;
      if (submerged) { sp.alpha = 0.5; sp.tint = '#a9c8e8'; } // same render, ghosted below the surface
      node.addChild(sp);
      // the ensign flies even submerged (ghosted with the hull, hull-only blue
      // cast) so the sub's owner stays readable
      const ft = legacyArt(box.snap) ? null : tex(ensignUrl(facing));
      const lay = ft ? ensignLayout(box.snap.type, facing, box.w, box.h, ft.width / ft.height) : null;
      if (ft && lay) {
        const f = node.addChild(new Sprite(ft));
        f.anchor.set(facing === 'right' ? 1 : 0, 0); // pole-top corner sits on the mast
        f.position.set(lay.fx, lay.fy);
        f.width = lay.fw; f.height = lay.fh;
        f.tint = box.side === 'R' ? COL.R : COL.Y;
        const gfx = node.addChild(new Graphics());
        const { gx, gy, gr } = lay;
        if (box.side === 'R') gfx.circle(gx, gy, gr).stroke({ width: gr * 0.55, color: 'rgba(255,255,255,0.95)' });
        else gfx.moveTo(gx - gr, gy - gr).lineTo(gx + gr, gy + gr).moveTo(gx + gr, gy - gr).lineTo(gx - gr, gy + gr)
          .stroke({ width: gr * 0.55, color: 'rgba(36,24,4,0.9)', cap: 'round' });
        if (submerged) { f.alpha = 0.55; gfx.alpha = 0.55; }
      }
      node.zIndex = box.h;
      shipSprites.set(box.id, node);
    }
  }

  // static sea — a vertical gradient (hazy horizon above the waterline, deep water
  // below) with the shared seamless ocean texture tiled over the water band. Built
  // once; never redraws. Replaces the old royal-blue brick sea, which clashed with
  // the photoreal ship renders.
  function buildSea(): void {
    const mix = (a: number, b: number, t: number): number => {
      const ar = a >> 16, ag = (a >> 8) & 255, ab = a & 255;
      const br = b >> 16, bg = (b >> 8) & 255, bb = b & 255;
      return ((ar + (br - ar) * t) << 16) | ((ag + (bg - ag) * t) << 8) | (ab + (bb - ab) * t);
    };
    const wh = SCENE_H - WATER_Y;
    seaG.clear();
    const N = 40;
    // backdrop above the waterline: deep navy at the top fading to a hazy teal horizon
    const sky0 = 0x0c1830, sky1 = 0x2a5877;
    for (let i = 0; i < N; i++) seaG.rect(0, (i / N) * WATER_Y, SCENE_W, WATER_Y / N + 1).fill(mix(sky0, sky1, i / N));
    // deep water behind the ships (shows in the gaps): surface = the board's sea
    // fill (SEA_FILL #4a9fbe) so the battle sea matches the game board, darkening to
    // a deeper shade of the same hue toward the sea floor.
    const surf = 0x4a9fbe, deep = 0x235d76;
    for (let i = 0; i < N; i++) seaG.rect(0, WATER_Y + (i / N) * wh, SCENE_W, wh / N + 1).fill(mix(surf, deep, i / N));

    // sea surface (drawn BEHIND the ships): a translucent teal gradient + the
    // seamless ocean texture + a crisp foam line at the waterline. Gives the water
    // band surface detail and marks the waterline the hulls sit on.
    for (const c of waterFg.removeChildren()) c.destroy();
    const grad = waterFg.addChild(new Graphics());
    for (let i = 0; i < N; i++) grad.rect(0, WATER_Y + (i / N) * wh, SCENE_W, wh / N + 1).fill({ color: mix(surf, deep, i / N), alpha: 0.08 + 0.55 * (i / N) });
    const st = waterFg.addChild(new TilingSprite({ texture: tex('assets/sprites/sea.jpg'), width: SCENE_W, height: wh }));
    st.position.set(0, WATER_Y);
    st.tileScale.set(0.16);
    st.alpha = 0.16;
    st.tint = 0x8fb6cc;
    const foam = waterFg.addChild(new Graphics());
    foam.rect(0, WATER_Y - 1.5, SCENE_W, 2.5).fill({ color: 0xdcf0fa, alpha: 0.5 });   // bright waterline
    foam.rect(0, WATER_Y + 1, SCENE_W, 1.5).fill({ color: 0xbfe0f0, alpha: 0.22 });
  }
  buildSea();

  // ---- per-site cached layout + helpers ----
  const layoutCache = new Map<number, Map<number, ShipBox>>();
  const shotBeats = new Map<string, ShotBeat>();
  for (const b of tl.beats) if (b.kind === 'shot') shotBeats.set(`${b.siteIndex}:${b.eventIndex}`, b);
  const impactTime = (si: number, ei: number): number => {
    const b = shotBeats.get(`${si}:${ei}`);
    return b ? b.start + (b.end - b.start) * IMPACT : Infinity;
  };

  function layoutFor(si: number): Map<number, ShipBox> {
    const cached = layoutCache.get(si);
    if (cached) return cached;
    const site = sites[si]!;
    const map = new Map<number, ShipBox>();
    for (const side of ['R', 'Y'] as Side[]) {
      const isLeft = side === viewSide;
      const ships = site.before
        .filter((s) => s.side === side)
        .sort((a, b) => b.maxHp - a.maxHp); // biggest frontmost
      ships.forEach((snap, i) => {
        const scale = 1 - i * 0.16;
        const wpx = Math.min(MAX_CINE_W, (CINE_W[snap.type] ?? 150) * scale);
        const t = tex(spriteUrl(snap, facingOf(snap)));
        const aspect = t && t.height ? t.width / t.height : 2.4;
        const h = wpx / aspect;
        const baseX = isLeft ? SCENE_W * 0.24 : SCENE_W * 0.76;
        const x = baseX + (isLeft ? -1 : 1) * i * (wpx * 0.42 + 26);
        // seat the sprite on the FOREGROUND sea line (SHIP_Y, low in the scene), not
        // the far horizon — so raising the sea line doesn't drag the fleet up. The
        // keel sits ~on SHIP_Y and the whole ship draws on top of the sea. Ranked
        // ships step UP as they recede (like the horizontal stagger).
        const cy = SHIP_Y - h * 0.34 - i * 14;
        map.set(snap.id, {
          id: snap.id, snap, side, x, cy, w: wpx, h,
          faceDir: isLeft ? 1 : -1,
        });
      });
    }
    layoutCache.set(si, map);
    return map;
  }

  // ---- drawing primitives (Pixi) ----
  // Ships are retained sprites (transforms set in render); the sea is static
  // (buildSea above). These helpers draw the per-frame TRANSIENT effects — only
  // ever one active shot at a time — into fxG / the glow sprites / the text nodes.
  const sideColNum = (s: Side): number => (s === 'R' ? 0xe0584a : 0xe9c247);

  function drawHpBar(box: ShipBox, hp: number): void {
    const w = Math.max(40, box.w * 0.5);
    const x = box.x - w / 2;
    const y = box.cy - box.h * 0.5 - 16;
    const frac = Math.max(0, hp / box.snap.maxHp);
    hpG.rect(x - 1, y - 1, w + 2, 7).fill({ color: 0x000000, alpha: 0.55 });
    hpG.rect(x, y, w * frac, 5).fill(frac > 0.5 ? 0x5fd06a : frac > 0.25 ? 0xe8c14a : 0xe0503a);
    hpG.rect(x, y, w, 5).stroke({ width: 1, color: 0xffffff, alpha: 0.4 });
    hpG.rect(x - 6, y, 4, 5).fill(sideColNum(box.side)); // side tab
  }

  function drawMuzzle(mx: number, my: number, dir: number, k: number, seed: number): void {
    const rng = makeRng(seed);
    const r = 16 * (0.6 + k * 0.6);
    muzzleGlow.visible = true;
    muzzleGlow.position.set(mx, my);
    muzzleGlow.tint = 0xffc24a;
    muzzleGlow.scale.set((r * 1.7) / 48);
    muzzleGlow.alpha = k;
    // smoke puffs drifting up/forward
    for (let i = 0; i < 4; i++) {
      const px = mx + dir * (6 + i * 6) + rng.int(6) - 3;
      const py = my - i * 4 - rng.int(4);
      fxG.circle(px, py, 3 + i).fill({ color: 0xb4b4b9, alpha: 0.4 * (1 - k * 0.5) });
    }
  }

  function drawShell(ax: number, ay: number, tx: number, ty: number, p: number): void {
    const x = ax + (tx - ax) * p;
    const arcH = Math.hypot(tx - ax, ty - ay) * 0.22;
    const y = ay + (ty - ay) * p - arcH * 4 * p * (1 - p);
    const pp = Math.max(0, p - 0.08);
    const px = ax + (tx - ax) * pp;
    const py = ay + (ty - ay) * pp - arcH * 4 * pp * (1 - pp);
    fxG.moveTo(px, py).lineTo(x, y).stroke({ width: 2, color: 0xffdc96, alpha: 0.7 }); // tracer
    fxG.circle(x, y, 3).fill(0xfff3c0);
  }

  function drawTorpedo(ax: number, tx: number, p: number, seed: number): void {
    const rng = makeRng(seed);
    const y = SHIP_Y + 4;
    const x = ax + (tx - ax) * p;
    // dashed wake (manual dashes — Pixi Graphics has no line dash)
    const lo = Math.min(ax, x); const hi = Math.max(ax, x);
    for (let dx = lo; dx < hi; dx += 9) fxG.moveTo(dx, y).lineTo(Math.min(dx + 5, hi), y);
    fxG.stroke({ width: 2, color: 0xffffff, alpha: 0.55 });
    // bubbles
    for (let i = 0; i < 5; i++) {
      const bx = ax + (x - ax) * (i / 5);
      fxG.circle(bx, y + rng.int(4) - 2, 1.5).fill({ color: 0xffffff, alpha: 0.5 });
    }
    fxG.rect(x - 4, y - 2, 8, 4).fill(0xdfe7ee); // torpedo head
  }

  function drawPlane(ax: number, ay: number, tx: number, ty: number, p: number, dir: number): void {
    // fly high from above attacker toward target, then dive at the end
    const fly = Math.min(1, p / IMPACT);
    const hx = ax + (tx - ax) * fly;
    const topY = Math.min(ay, ty) - 70;
    const hy = topY + (p > IMPACT ? (ty - 14 - topY) * ((p - IMPACT) / (1 - IMPACT)) : 0);
    fxG.ellipse(hx, hy, 11, 3.4).fill(0xe9edf2); // fuselage
    fxG.rect(hx - 3, hy - 8, 6, 16).fill(0xb9c2cc); // wings (symmetric → no flip needed)
    fxG.rect(hx + (dir < 0 ? 8 : -11), hy - 5, 3, 10).fill(0x7d8893); // tail (mirrored by dir)
    if (p > IMPACT + 0.1) { // dropped ordnance on the dive
      const dp = (p - IMPACT - 0.1) / (1 - IMPACT - 0.1);
      fxG.circle(hx + dir * 6, hy + 8 + dp * 14, 2.5).fill(0x3a3f45);
    }
  }

  function drawExplosion(x: number, y: number, k: number, seed: number): void {
    const rng = makeRng(seed);
    const r = 8 + k * 34;
    explosionGlow.visible = true;
    explosionGlow.position.set(x, y);
    explosionGlow.tint = 0xffa028;
    explosionGlow.scale.set((r * 1.6) / 48);
    explosionGlow.alpha = 0.9 * (1 - k);
    for (let i = 0; i < 9; i++) { // sparks
      const a = rng.next() * Math.PI * 2;
      const d = r * (0.5 + rng.next() * 0.7);
      const col = (255 << 16) | ((160 + rng.int(80)) << 8) | 60;
      fxG.circle(x + Math.cos(a) * d, y + Math.sin(a) * d, 1.6).fill({ color: col, alpha: 1 - k });
    }
  }

  function drawSplash(x: number, k: number): void {
    const y = SHIP_Y;
    for (let i = -2; i <= 2; i++) fxG.moveTo(x + i * 5, y).quadraticCurveTo(x + i * 7, y - 18 * (1 - k * 0.6), x + i * 9, y);
    fxG.stroke({ width: 2, color: 0xe6f5ff, alpha: 1 - k });
  }

  function drawDamageFloat(x: number, y: number, ev: CombatEvent, k: number): void {
    dmgText.visible = true;
    dmgText.text = ev.sunk ? `SUNK! −${ev.dmg}` : `−${ev.dmg}`;
    dmgText.style.fill = ev.attackerSide === 'R' ? COL.Rlabel : COL.Ylabel;
    dmgText.position.set(x, y - k * 34);
    dmgText.alpha = 1 - k;
  }

  function drawAirBadge(x: number, y: number, n: number): void {
    airText.visible = true;
    airText.text = `✈ +${n} Air`;
    airText.style.fill = '#cfeaff';
    airText.position.set(x, y - 4);
    const w = airText.width + 14;
    fxG.roundRect(x - w / 2, y - 14, w, 20, 5).fill({ color: 0x142339, alpha: 0.85 }).stroke({ width: 1.5, color: 0x8fd0ff });
  }

  // ---- the frame ----
  function render(ms: number): void {
    const beat = beatAt(tl, ms);
    const si = beat ? beat.siteIndex : 0;
    const site = sites[si];

    // screen shake from a recent impact (offsets the whole scene container)
    let shx = 0;
    let shy = 0;
    if (beat && beat.kind === 'shot') {
      const p = beatProgress(beat, ms);
      if (p >= IMPACT && p < IMPACT + 0.16) {
        const k = 1 - (p - IMPACT) / 0.16;
        const rng = makeRng(beat.seed ^ 0x9e37);
        const mag = (3 + Math.min(8, beat.event.dmg)) * k;
        shx = (rng.next() - 0.5) * 2 * mag;
        shy = (rng.next() - 0.5) * 2 * mag;
      }
    }
    shakeRoot.position.set(shx, shy);

    // reset per-frame layers (static sea + retained ship sprites persist)
    hpG.clear();
    fxG.clear();
    muzzleGlow.visible = false;
    explosionGlow.visible = false;
    dmgText.visible = false;
    airText.visible = false;

    if (!site) {
      ensureShips(-1);
      updateHud(ms, si, null, null);
      app.render();
      return;
    }
    ensureShips(si);
    const layout = layoutFor(si);

    // how many shots have already landed in this site
    let fired = 0;
    for (let i = 0; i < site.events.length; i++) if (impactTime(si, i) <= ms) fired++;
    const hpMap = hpAfter(site, fired);

    // sink event index per ship (first event that drops it to ≤0)
    const sinkIdx = new Map<number, number>();
    {
      const work = hpAfter(site, 0);
      site.events.forEach((ev, i) => {
        const e = work.get(ev.targetId);
        if (e && !e.sunk) {
          e.hp -= ev.dmg;
          if (e.hp <= 0) {
            e.sunk = true;
            sinkIdx.set(ev.targetId, i);
          }
        }
      });
    }

    // position each retained ship sprite (sink → drop + tilt + fade); z-order by
    // size (set in ensureShips) keeps capitals in front.
    for (const box of layout.values()) {
      const sp = shipSprites.get(box.id);
      if (!sp) continue;
      const st = hpMap.get(box.id);
      const sIdx = sinkIdx.get(box.id);
      let sinking = false;
      if (sIdx !== undefined) {
        const since = ms - impactTime(si, sIdx);
        if (since > 0) {
          const k = Math.min(1, since / 1100);
          sp.position.set(box.x, box.cy + k * box.h * 0.7);
          sp.rotation = box.faceDir * k * 0.5;
          sp.alpha = 1 - k * 0.85;
          sinking = true;
          if (k < 0.6) drawSplash(box.x, k / 0.6);
        }
      }
      if (!sinking) {
        sp.position.set(box.x, box.cy);
        sp.rotation = 0;
        sp.alpha = 1;
        if (st && st.hp > 0) drawHpBar(box, st.hp);
      }
    }

    // active shot effects
    if (beat && beat.kind === 'shot') {
      const p = beatProgress(beat, ms);
      const ev = beat.event;
      const A = layout.get(ev.attackerId);
      const T = layout.get(ev.targetId);
      if (A && T) {
        const mx = A.x + A.faceDir * A.w * 0.42;
        const my = A.cy - A.h * 0.12;
        const tx = T.x;
        const ty = T.cy - T.h * 0.1;
        // muzzle flash early — gun ships only (carriers launch planes, subs torpedoes)
        if (p < 0.22 && beat.weapon === 'shell') drawMuzzle(mx, my, A.faceDir, 1 - p / 0.22, beat.seed);
        // projectile in flight
        if (p < IMPACT) {
          const fp = p / IMPACT;
          if (beat.weapon === 'torpedo') drawTorpedo(mx, tx, fp, beat.seed);
          else if (beat.weapon === 'air') drawPlane(mx, my, tx, ty, p, A.faceDir);
          else drawShell(mx, my, tx, ty, fp);
        } else {
          if (beat.weapon === 'air') drawPlane(mx, my, tx, ty, p, A.faceDir);
          const k = (p - IMPACT) / (1 - IMPACT);
          drawExplosion(tx, ty, k, beat.seed);
          drawDamageFloat(tx, ty - T.h * 0.4, ev, k);
        }
        if (beat.weapon === 'air' && ev.airBonus > 0) {
          drawAirBadge(A.x, A.cy - A.h * 0.5 - 26, ev.airBonus);
        }
      }
    }

    updateHud(ms, si, site, beat);
    app.render();
  }

  function shipName(snap: CombatShipSnapshot | undefined): string {
    return snap ? UNITS[snap.type].name : 'ship';
  }

  /** Ships still afloat per side in a site at time `ms` (reflects shots landed). */
  function survivorsAt(site: CombatSite, si: number, ms: number): Record<Side, number> {
    let fired = 0;
    for (let i = 0; i < site.events.length; i++) if (impactTime(si, i) <= ms) fired++;
    const hp = hpAfter(site, fired);
    const n: Record<Side, number> = { R: 0, Y: 0 };
    for (const e of hp.values()) if (e.hp > 0) n[e.snap.side]++;
    return n;
  }

  function updateHud(ms: number, si: number, site: CombatSite | null, beat: Beat | null): void {
    // title: swords glyph + "Battle", plus which duel of how many we're watching.
    $('ar-cine-title').innerHTML = site && sites.length > 1
      ? `${ICON_SWORDS}Battle <span style="color:hsl(215,100%,90.6%);font-weight:400">· Duel ${si + 1} / ${sites.length}</span>`
      : `${ICON_SWORDS}Battle`;
    const ev = beat && beat.kind === 'shot' ? beat.event : null;
    const lastSite = si >= sites.length - 1;
    let sub: string;
    if (ev && site) {
      const a = site.before.find((s) => s.id === ev.attackerId);
      const t = site.before.find((s) => s.id === ev.targetId);
      // team chips (colour + shape) stand in for the old "Red"/"Yellow" names.
      sub = `${teamChip(ev.attackerSide)} ${shipName(a)} hits ${teamChip(ev.targetSide)} ${shipName(t)} for ${ev.dmg}${ev.airBonus > 0 ? ` (+${ev.airBonus} air)` : ''}${ev.sunk ? ' — <b>SUNK!</b>' : ''}`;
    } else if (ms >= tl.duration || (beat && beat.kind === 'tail' && lastSite)) {
      sub = 'Battle resolved.';
    } else if (beat && beat.kind === 'tail') {
      sub = 'The smoke clears…';
    } else {
      sub = 'The fleets close to firing range…';
    }
    $('ar-cine-sub').innerHTML = sub;
    // fleet tally scoreboard — the two sides' ships still afloat in this duel.
    const tally = $('ar-cine-tally');
    if (site) {
      const n = survivorsAt(site, si, ms);
      const cell = (s: Side): string =>
        `<span class="team ${s}${n[s] === 0 ? ' gone' : ''}">${teamChip(s)}×${n[s]}</span>`;
      // viewer's own side first, so "your" fleet reads left-to-right consistently.
      tally.innerHTML = viewSide === 'R' ? cell('R') + cell('Y') : cell('Y') + cell('R');
    } else {
      tally.innerHTML = '';
    }
    (($('ar-cine-prog') as HTMLElement).style.width = `${tl.duration ? Math.min(100, (ms / tl.duration) * 100) : 100}%`);
    // highlight the active site chip
    root.querySelectorAll('.ar-cine-chip').forEach((c, i) => c.classList.toggle('on', i === si));
  }

  // ---- site list chips (#1 crcr · dddd) — the roster of duels ----
  function buildChips(): void {
    const host = $('ar-cine-sites');
    host.innerHTML = '<span class="ar-cine-sites-cap">Combat sites — click to replay</span>';
    sites.forEach((site, i) => {
      const end = hpAfter(site, site.events.length);
      const codeFor = (s: CombatShipSnapshot): string => {
        const sunk = end.get(s.id)?.sunk;
        const code = sunk ? '--' : BIG.has(s.type) ? s.type : s.type.toLowerCase();
        return `<span class="${s.side}">${code}</span>`;
      };
      const mine = site.before.filter((s) => s.side === viewSide).map(codeFor).join('');
      const foe = site.before.filter((s) => s.side !== viewSide).map(codeFor).join('');
      const chip = document.createElement('button');
      chip.className = 'ar-cine-chip';
      chip.innerHTML = `<span class="n">#${i + 1}</span>${mine}<span class="gap">·</span>${foe}`;
      chip.addEventListener('click', () => api.gotoSite(i));
      host.appendChild(chip);
    });
  }

  // ---- clock / controls ----
  let elapsed = 0;
  let playing = opts.autoplay !== false;
  let speed = 1;
  let last = performance.now();
  let alive = true;
  let finished = false;
  let finishedAt = 0;
  let raf = 0;

  /** Fire the gun report at each shot's launch and the explosion/sink at impact,
   *  for shots whose moment falls in (prev, now]. Live playback only — seeking
   *  jumps `elapsed`, so the next frame's small (prev, now] window replays nothing. */
  function playSfx(prev: number, now: number): void {
    for (const b of tl.beats) {
      if (b.kind !== 'shot') continue;
      if (b.start > prev && b.start <= now) {
        if (b.weapon !== 'air') audio.playFire(b.event.attackerType as ShipType);
      }
      const it = impactTime(b.siteIndex, b.eventIndex);
      if (it > prev && it <= now) {
        if (b.event.sunk) audio.playSink();
        else audio.playExplosion();
      }
    }
  }

  // Responsive fit: size the scene to the STAGE's aspect (fixed width, derived
  // height) so the canvas fills the stage with no dead margins, then scale to fit.
  // SCENE_H drives the sky/water bands + ship seating, so a changed height rebuilds
  // the sea and clears the cached per-site layout. Height is clamped so extreme
  // window shapes keep a sane composition (letterboxing only past the clamp).
  function fit(): void {
    const availW = stage.clientWidth - 20;
    const availH = stage.clientHeight - 20;
    if (availW <= 0 || availH <= 0) return;
    const newH = Math.round(Math.min(560, Math.max(300, SCENE_W * (availH / availW))));
    if (newH !== SCENE_H) {
      SCENE_H = newH;
      WATER_Y = SCENE_H * WATER_FRAC;
      SHIP_Y = SCENE_H * SHIP_FRAC;
      buildSea();
      layoutCache.clear();
      builtSite = -1; // force ensureShips to re-seat sprites at the new waterline
    }
    const scale = Math.min(availW / SCENE_W, availH / SCENE_H);
    const dw = Math.round(SCENE_W * scale);
    const dh = Math.round(SCENE_H * scale);
    app.renderer.resize(dw, dh);
    canvas.style.width = `${dw}px`;
    canvas.style.height = `${dh}px`;
    app.stage.scale.set(dw / SCENE_W, dh / SCENE_H);
    render(elapsed);
  }
  const resizeObs = new ResizeObserver(() => fit());

  function teardown(): void {
    if (!alive) return;
    alive = false;
    resizeObs.disconnect();
    window.removeEventListener('resize', fit);
    cancelAnimationFrame(raf);
    // Release the GPU: a fresh viewer + WebGL context mounts every end-of-turn
    // combat, and browsers cap live contexts (~16) then force-lose the oldest —
    // so without this, late-game battles eventually fail to render. Destroy the
    // Application (renderer/context + scene children) but NOT the ship textures —
    // those come from the shared global Assets cache and are reused next battle;
    // only the locally-baked glow texture is ours to free.
    app.destroy({ removeView: false }, { children: true, texture: false });
    glowTex.destroy(true);
    root.remove();
  }
  function finishAndClose(): void {
    teardown();
    onDone();
  }

  function updateButtons(): void {
    $('ar-cine-pause').innerHTML = playing ? `${ICON_PAUSE} Pause` : `${ICON_PLAY} Play`;
    $('ar-cine-speed').textContent = `Speed ${speed}×`;
    const skip = $('ar-cine-skip');
    if (finished) {
      skip.textContent = 'Continue ▸';
      skip.classList.add('primary');
    }
  }

  function loop(now: number): void {
    if (!alive) return;
    if (playing) {
      const prev = elapsed;
      elapsed += (now - last) * speed;
      playSfx(prev, elapsed);
    }
    last = now;
    if (elapsed >= tl.duration) {
      elapsed = tl.duration;
      if (!finished) {
        finished = true;
        finishedAt = now;
        updateButtons();
      } else if (playing && now - finishedAt > 800) {
        finishAndClose();
        return;
      }
    }
    render(elapsed);
    raf = requestAnimationFrame(loop);
  }

  $('ar-cine-pause').addEventListener('click', () => {
    playing = !playing;
    last = performance.now();
    if (playing && !raf) raf = requestAnimationFrame(loop); // kick the loop if it never started (paused mount)
    updateButtons();
  });
  $('ar-cine-speed').addEventListener('click', () => {
    speed = speed === 1 ? 2 : speed === 2 ? 4 : 1;
    updateButtons();
  });
  $('ar-cine-skip').addEventListener('click', finishAndClose);
  // guard beatAt — an empty (zero-beat) timeline returns null, and jumping duels
  // with no beats would otherwise throw.
  $('ar-cine-prev').addEventListener('click', () => { const b = beatAt(tl, elapsed); if (b) api.gotoSite(b.siteIndex - 1); });
  $('ar-cine-next').addEventListener('click', () => { const b = beatAt(tl, elapsed); if (b) api.gotoSite(b.siteIndex + 1); });
  const skipFut = $('ar-cine-skipfut') as HTMLInputElement;
  skipFut.checked = getSkipCinematic();
  skipFut.addEventListener('change', () => setSkipCinematic(skipFut.checked));

  buildChips();
  updateButtons();

  const api: CombatViewerHandle = {
    el: root,
    duration: tl.duration,
    seek(ms) {
      elapsed = Math.max(0, Math.min(tl.duration, ms));
      finished = elapsed >= tl.duration;
      render(elapsed);
      updateButtons();
    },
    pause() {
      playing = false;
      updateButtons();
    },
    play() {
      playing = true;
      last = performance.now();
      if (!raf) raf = requestAnimationFrame(loop); // kick the loop if it never started
      updateButtons();
    },
    setSpeed(mult) {
      speed = mult;
      updateButtons();
    },
    gotoSite(i) {
      const idx = Math.max(0, Math.min(sites.length - 1, i));
      const span = tl.sites[idx];
      if (span) {
        elapsed = span.start;
        finished = false;
        last = performance.now();
        render(elapsed);
        updateButtons();
      }
    },
    skip: finishAndClose,
  };

  fit();
  resizeObs.observe(stage);
  window.addEventListener('resize', fit);
  render(elapsed);
  if (playing) raf = requestAnimationFrame(loop);

  return api;
}
